import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Check, RefreshCw } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sid) {
      setStatus("error");
      return;
    }
    let mounted = true;
    let timeout;
    const poll = async (n) => {
      if (!mounted) return;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (n >= 8) {
          setStatus("timeout");
          return;
        }
        setAttempts(n + 1);
        timeout = setTimeout(() => poll(n + 1), 2000);
      } catch {
        if (n >= 8) {
          setStatus("error");
          return;
        }
        timeout = setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { mounted = false; if (timeout) clearTimeout(timeout); };
  }, [sid]);

  return (
    <div className="min-h-screen grid place-items-center bg-obsidian text-white px-6">
      <div className="w-full max-w-md sr-card p-8 sr-fade-in" data-testid="billing-success-card">
        <div className="mb-6"><Logo size={36} /></div>
        {status === "polling" && (
          <>
            <RefreshCw className="h-6 w-6 text-teal animate-spin mb-4" />
            <h1 className="font-display text-xl text-white tracking-tight">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-white/55">Attempt {attempts + 1}/9 — this usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="h-9 w-9 rounded-full bg-teal/20 border border-teal grid place-items-center mb-4">
              <Check className="h-5 w-5 text-teal" />
            </div>
            <h1 className="font-display text-2xl text-white tracking-tight">You're in.</h1>
            <p className="mt-2 text-sm text-white/55">Premium activated. Full access restored — no daily limits, all 9 modes.</p>
            <Button
              data-testid="back-to-workspace-btn"
              onClick={() => nav("/dashboard")}
              className="mt-6 w-full bg-teal hover:bg-teal-hover text-white rounded-sm"
            >
              Open Workspace
            </Button>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <h1 className="font-display text-xl text-white tracking-tight">Payment not confirmed</h1>
            <p className="mt-2 text-sm text-white/55">
              {status === "expired"
                ? "The session expired. Please try again."
                : status === "timeout"
                ? "We didn't get confirmation in time. Check your email for receipt."
                : "Something went wrong. Please retry from the workspace."}
            </p>
            <Button
              data-testid="back-to-workspace-btn"
              onClick={() => nav("/dashboard")}
              className="mt-6 w-full bg-teal hover:bg-teal-hover text-white rounded-sm"
            >
              Back to Workspace
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
